import g3 from "@/assets/gallery-new-3.webp";
import v1 from "@/assets/gallery-video-1.webm";
import v4 from "@/assets/gallery-video-4.webm";
import v5 from "@/assets/gallery-video-5.webm";
import g1Webp from "@/assets/optimized/gallery-new-1.webp";
import g10Webp from "@/assets/optimized/gallery-new-10.webp";
import g11Webp from "@/assets/optimized/gallery-new-11.webp";
import g12Webp from "@/assets/optimized/gallery-new-12.webp";
import g13Webp from "@/assets/optimized/gallery-new-13.webp";
import g14Webp from "@/assets/optimized/gallery-new-14.webp";
import g15Webp from "@/assets/optimized/gallery-new-15.webp";
import g16Webp from "@/assets/optimized/gallery-new-16.webp";
import g17Webp from "@/assets/optimized/gallery-new-17.webp";
import g2Webp from "@/assets/optimized/gallery-new-2.webp";
import g3Webp from "@/assets/optimized/gallery-new-3.webp";
import g4Webp from "@/assets/optimized/gallery-new-4.webp";
import g5Webp from "@/assets/optimized/gallery-new-5.webp";
import LazyVideo from "@/components/LazyVideo";
import OptimizedImage from "@/components/OptimizedImage";
import Reveal from "@/components/Reveal";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { APP_URL } from "@/lib/config";
import { AlignLeft, Copy, Download, Sparkles, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";

type Media = {
    src: string;
    type: "image" | "video";
    ratio: number;
    poster?: string;
    webpSrc?: string;
    posterWebp?: string;
    alt?: string;
};
const img = (src: string, ratio: number, webpSrc?: string): Media => ({
    src,
    type: "image",
    ratio,
    webpSrc,
});
const vid = (
    src: string,
    ratio = 9 / 16,
    poster?: string,
    posterWebp?: string,
): Media => ({
    src,
    type: "video",
    ratio,
    poster,
    posterWebp,
});

interface GallerySectionProps {
    enableModal?: boolean;
    /** Heading text shown above the gallery. */
    heading?: string;
    /** Render the heading as the page <h1> (standalone /gallery) or a section
     *  <h2> (when embedded on a page that already has its own <h1>). */
    headingAs?: "h1" | "h2";
    /** Intro copy under the heading. Standalone pages pass richer, keyword-rich
     *  copy (avoids a thin /gallery page); embedded uses keep the short default. */
    description?: string;
}

const SAMPLE_PROMPT =
    "Front and back view of a light beige hooded jacket, studio catalogue style.\nThe jacket is made from lightweight, slightly shiny nylon or polyester fabric.\nFront view shows a central zipper closure, two functional side pockets, elastic cuffs, and an elastic hem for a snug fit.";

const SAMPLE_DETAILS = [
    { label: "Tool", value: "Catalogue Tryon" },
    { label: "Background", value: "Beige background", swatch: "#E8DDC8" },
    { label: "Size", value: "3:4" },
    { label: "Model", value: "Indian" },
    { label: "Pose", value: "Back pose" },
    { label: "Created on", value: "24 Jan 2026" },
];

// ratio = width / height. Pinterest-style: each tile keeps its natural aspect.
// Mixed and shuffled for an organic masonry feel.
const items: Media[] = [
    img(g1Webp, 3 / 4, undefined),
    vid(v1, 9 / 16, g2Webp),
    img(g14Webp, 9 / 16),
    img(g11Webp, 1 / 1),
    vid(
        "https://cdn.pixro.ai/220f2ea3-383e-44a6-9598-c96f1708b753.mp4",
        9 / 16,
        g11Webp,
    ),
    img(g2Webp, 3 / 4),
    img(g4Webp, 3 / 4),
    img(g15Webp, 9 / 16),
    vid(
        "https://cdn.pixro.ai/91e6f7a5-1f12-470d-85ac-fa6d26583ff9.mp4",
        3 / 4,
        g4Webp,
    ),
    img(g16Webp, 9 / 16),
    vid(v4, 9 / 16, g3Webp),
    img(g3, 3 / 4, g3Webp),
    img(g17Webp, 9 / 16),
    vid(v5, 9 / 16, g10Webp),
    img(g12Webp, 3 / 4),
    img(g10Webp, 1 / 1.2),
    vid(
        "https://cdn.pixro.ai/394dddbc-d1d4-4065-aeab-8baaf7f56630.mp4",
        3 / 4,
        g5Webp,
    ),
    img(g13Webp, 3 / 4),
    img(g5Webp, 1 / 1),
    vid(
        "https://cdn.pixro.ai/805ebcef-1a6b-42a0-ac87-31587c71e476.mp4",
        9 / 16,
        g1Webp,
    ),
];

const IMAGE_ALTS: Record<number, string> = {
    0: "AI fashion model wearing a beige hooded jacket, catalogue tryon by Pixro",
    2: "AI product photo of a white sneaker on a clean gradient background",
    3: "Ghost mannequin effect on a structured blazer showing collar and shoulder detail",
    5: "Studio-quality flat-lay of a folded denim jacket on a white surface",
    6: "AI lifestyle scene with a handbag placed on a marble countertop",
    7: "Virtual try-on of a floral summer dress on an AI fashion model",
    9: "Ecommerce product shot of a wristwatch on a dark minimal background",
    11: "AI model shoot of a hoodie in an urban outdoor lifestyle setting",
    12: "Catalogue photo of running shoes with a clean white background",
    14: "AI-generated CGI product visual of a perfume bottle with dramatic lighting",
    15: "Close-up product shot of jewellery on a dark velvet background",
    17: "AI lifestyle image of sunglasses on a sandy beach background",
    18: "Flat-lay of a folded graphic tee with accessory styling",
};

const GallerySection = ({
    enableModal = false,
    heading = "What you can create from Pixro",
    headingAs: HeadingTag = "h2",
    description = "Real visuals created with Pixro, explore what's possible and spark your next idea.",
}: GallerySectionProps = {}) => {
    const sectionRef = useRef<HTMLElement | null>(null);
    const sentinelRef = useRef<HTMLDivElement | null>(null);
    const galleryTopRef = useRef<HTMLDivElement | null>(null);
    const [showButton, setShowButton] = useState(false);
    const [ctaVisible, setCtaVisible] = useState(false);
    const [activeItem, setActiveItem] = useState<Media | null>(null);

    const handleCopy = () => {
        navigator.clipboard?.writeText(SAMPLE_PROMPT);
        toast.success("Copied to clipboard");
    };

    const handleDownload = async () => {
        if (!activeItem) return;
        try {
            const res = await fetch(activeItem.src);
            const blob = await res.blob();
            const url = URL.createObjectURL(blob);
            const ext =
                activeItem.type === "video"
                    ? "mp4"
                    : blob.type.split("/")[1] || "jpg";
            const a = document.createElement("a");
            a.href = url;
            a.download = `pixro-${Date.now()}.${ext}`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
        } catch {
            toast.error("Download failed");
        }
    };

    useEffect(() => {
        const sentinel = sentinelRef.current;
        if (!sentinel) return;
        const observer = new IntersectionObserver(
            ([entry]) => {
                setShowButton(
                    entry.isIntersecting || entry.boundingClientRect.top < 0,
                );
            },
            { threshold: 0, rootMargin: "0px 0px -40% 0px" },
        );
        observer.observe(sentinel);
        return () => observer.disconnect();
    }, []);

    useEffect(() => {
        const top = galleryTopRef.current;
        if (!top) return;
        const onScroll = () => {
            const rect = top.getBoundingClientRect();
            setCtaVisible(rect.top <= 0);
        };
        window.addEventListener("scroll", onScroll, { passive: true });
        onScroll();
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    return (
        <section
            ref={sectionRef}
            className="relative bg-background pt-12 sm:pt-16 pb-16 sm:pb-24"
        >
            <div className="container mx-auto px-6 mb-8 sm:mb-10 text-center">
                <Reveal variant="fade-up">
                    <HeadingTag className="text-3xl font-extrabold tracking-tight text-foreground sm:text-4xl">
                        {heading}
                    </HeadingTag>
                    <p className="mt-3 text-sm sm:text-base text-muted-foreground max-w-2xl mx-auto">
                        {description}
                    </p>
                </Reveal>
            </div>

            {/* Pinterest-style masonry using CSS columns */}
            <div className="relative px-1">
                <div
                    ref={galleryTopRef}
                    className="absolute left-0 right-0 top-0 h-1 pointer-events-none"
                    aria-hidden="true"
                />
                <div className="columns-2 sm:columns-3 md:columns-5 gap-1 [column-fill:_balance]">
                    {items.map((item, i) => {
                        const imageAlt =
                            IMAGE_ALTS[i] ??
                            `AI-generated product visual created with Pixro, gallery item ${i + 1}`;
                        const inner =
                            item.type === "video" ? (
                                <LazyVideo
                                    src={item.src}
                                    poster={item.poster ?? g1Webp}
                                    aria-hidden="true"
                                    className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.02] pointer-events-none"
                                />
                            ) : (
                                <OptimizedImage
                                    src={item.src}
                                    webpSrc={item.webpSrc}
                                    alt={imageAlt}
                                    loading="lazy"
                                    width={800}
                                    height={1000}
                                    className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.02] pointer-events-none"
                                />
                            );
                        return (
                            <div
                                key={i}
                                className="mb-1 break-inside-avoid overflow-hidden"
                                style={{ aspectRatio: item.ratio }}
                            >
                                {enableModal ? (
                                    <button
                                        type="button"
                                        onClick={() => setActiveItem(item)}
                                        className="group relative block h-full w-full cursor-pointer overflow-hidden p-0 m-0 border-0 bg-transparent"
                                        aria-label={`Open gallery item ${i + 1}`}
                                    >
                                        {inner}
                                    </button>
                                ) : (
                                    inner
                                )}
                            </div>
                        );
                    })}
                </div>
                <div
                    ref={sentinelRef}
                    className="pointer-events-none absolute left-0 right-0"
                    style={{ top: "30%", height: "1px" }}
                    aria-hidden="true"
                />
            </div>

            {/* Sticky CTA that morphs from text to button */}
            <div className="pointer-events-none sticky bottom-6 sm:bottom-10 z-20 mt-[-100px] sm:mt-[-140px] flex justify-center px-6">
                <a
                    href={APP_URL}
                    className={`whitespace-nowrap font-bold transition-[opacity,transform,background-color,color,padding,box-shadow] duration-500 ease-out ${
                        ctaVisible
                            ? "opacity-100 pointer-events-auto"
                            : "opacity-0 pointer-events-none"
                    } ${
                        showButton
                            ? "rounded-[2rem] bg-[hsl(135,90%,65%)] px-8 py-4 text-lg sm:px-16 sm:py-6 sm:text-2xl md:px-24 md:py-7 md:text-3xl text-black shadow-[0_20px_60px_-10px_hsl(135,90%,55%,0.6)] scale-100"
                            : "bg-transparent px-0 py-0 text-xl sm:text-2xl md:text-3xl text-foreground scale-95"
                    }`}
                >
                    Start creating now
                </a>
            </div>

            {/* Detail modal — only when enableModal is true (Gallery page) */}
            {enableModal && (
                <Dialog
                    open={!!activeItem}
                    onOpenChange={(open) => !open && setActiveItem(null)}
                >
                    <DialogContent className="max-w-4xl w-[95vw] max-h-[90vh] p-0 overflow-y-auto md:overflow-hidden border-0 bg-[#1a1a1a] rounded-2xl [&>button]:hidden">
                        <div className="grid grid-cols-1 md:grid-cols-[1.1fr,1fr] gap-0 md:max-h-[90vh]">
                            {/* Left: media */}
                            <div className="bg-[#0f0f0f] flex items-center justify-center p-4 md:p-6 md:max-h-[90vh] md:overflow-hidden">
                                <div className="w-full max-w-sm rounded-2xl overflow-hidden bg-black">
                                    {activeItem?.type === "video" ? (
                                        <LazyVideo
                                            src={activeItem.src}
                                            poster={activeItem.poster ?? g1Webp}
                                            className="w-full h-auto object-cover max-h-[80vh]"
                                            style={{
                                                aspectRatio: activeItem.ratio,
                                            }}
                                        />
                                    ) : (
                                        activeItem && (
                                            <OptimizedImage
                                                src={activeItem.src}
                                                webpSrc={activeItem.webpSrc}
                                                alt="Selected"
                                                width={800}
                                                height={1000}
                                                className="w-full h-auto object-cover max-h-[80vh]"
                                                style={{
                                                    aspectRatio:
                                                        activeItem.ratio,
                                                }}
                                            />
                                        )
                                    )}
                                </div>
                            </div>

                            {/* Right: details */}
                            <div className="relative p-6 md:p-8 text-white flex flex-col md:max-h-[90vh] md:overflow-y-auto">
                                {/* Header */}
                                <div className="flex items-center justify-between mb-6">
                                    <div className="flex items-center gap-2.5">
                                        <span className="flex h-9 w-9 items-center justify-center rounded-full bg-[hsl(135,90%,65%)] text-black font-bold text-sm">
                                            P
                                        </span>
                                        <span className="font-semibold text-base">
                                            Pixro AI
                                        </span>
                                    </div>
                                    <button
                                        onClick={() => setActiveItem(null)}
                                        className="flex h-9 w-9 items-center justify-center rounded-full border border-white/20 hover:bg-white/10 transition"
                                        aria-label="Close"
                                    >
                                        <X className="h-4 w-4" />
                                    </button>
                                </div>

                                {/* Prompt */}
                                <div>
                                    <div className="flex items-center justify-between mb-2 text-sm">
                                        <span className="flex items-center gap-2 text-white/80">
                                            <AlignLeft className="h-4 w-4" />{" "}
                                            Prompt
                                        </span>
                                        <button
                                            onClick={handleCopy}
                                            aria-label="Copy prompt"
                                            className="flex items-center gap-1.5 text-white/80 hover:text-white transition"
                                        >
                                            Copy{" "}
                                            <Copy className="h-3.5 w-3.5" />
                                        </button>
                                    </div>
                                    <div className="rounded-xl bg-white/[0.04] border border-white/10 p-4 text-sm text-white/75 leading-relaxed whitespace-pre-line">
                                        {SAMPLE_PROMPT}
                                    </div>
                                </div>

                                {/* Details */}
                                <div className="mt-6">
                                    <div className="flex items-center gap-2 mb-2 text-sm text-white/80">
                                        <AlignLeft className="h-4 w-4" />{" "}
                                        Details
                                    </div>
                                    <div className="rounded-xl bg-white/[0.04] border border-white/10 divide-y divide-white/10 text-sm">
                                        {SAMPLE_DETAILS.map((d) => (
                                            <div
                                                key={d.label}
                                                className="flex items-center justify-between px-4 py-3"
                                            >
                                                <span className="text-white/70">
                                                    {d.label}
                                                </span>
                                                <span className="flex items-center gap-2 text-white">
                                                    {d.swatch && (
                                                        <span
                                                            className="h-4 w-4 rounded-md border border-white/20"
                                                            style={{
                                                                background:
                                                                    d.swatch,
                                                            }}
                                                        />
                                                    )}
                                                    {d.value}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                {/* Buttons */}
                                <div className="mt-6 space-y-3">
                                    <a
                                        href={APP_URL}
                                        className="flex items-center justify-center gap-2 w-full rounded-xl bg-[hsl(135,90%,65%)] text-black font-semibold py-3.5 hover:opacity-90 transition"
                                    >
                                        <Sparkles className="h-4 w-4" /> Use
                                        this as reference
                                    </a>
                                    <button
                                        onClick={handleDownload}
                                        aria-label="Download selected gallery item"
                                        className="flex items-center justify-center gap-2 w-full rounded-xl border border-white/20 py-3 text-sm font-medium hover:bg-white/10 transition"
                                    >
                                        <Download className="h-4 w-4" />{" "}
                                        Download
                                    </button>
                                </div>
                            </div>
                        </div>
                    </DialogContent>
                </Dialog>
            )}
        </section>
    );
};

export default GallerySection;
